import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Clock,
  User,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileCheck,
  History,
  RotateCcw,
  X,
  AlertTriangle,
  Eye,
  FileWarning,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useEscapeKey } from '@/shared/hooks/useEscapeKey';
import { Permission } from '@/types/global.types';
import {
  fetchInstitutionalDocuments,
  uploadInstitutionalDocument,
  setCurrentVersion,
  deleteVersion,
  downloadDocumentVersion,
  fetchDocumentPreview,
  formatFileSize,
  formatDate,
  InstitutionalDocumentType,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_DESCRIPTIONS,
  type InstitutionalDocument,
  type InstitutionalDocumentVersion,
} from '@/services/institutionalDocumentsService';

// Modal de confirmation
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  // Close modal on Escape key press
  useEscapeKey(onCancel, isOpen);

  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
      iconBg: 'bg-red-100',
      confirmBtn: 'bg-red-600 hover:bg-red-700',
    },
    warning: {
      icon: <AlertCircle className="w-6 h-6 text-amber-600" />,
      iconBg: 'bg-amber-100',
      confirmBtn: 'bg-amber-600 hover:bg-amber-700',
    },
    info: {
      icon: <AlertCircle className="w-6 h-6 text-blue-600" />,
      iconBg: 'bg-blue-100',
      confirmBtn: 'bg-blue-600 hover:bg-blue-700',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${styles.iconBg}`}>
              {styles.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <div className="text-sm text-gray-600">{message}</div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${styles.confirmBtn}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal d'upload
interface UploadModalProps {
  isOpen: boolean;
  documentType: InstitutionalDocumentType | null;
  onClose: () => void;
  onUpload: (file: File, notes: string) => Promise<void>;
  uploading: boolean;
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  documentType,
  onClose,
  onUpload,
  uploading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close modal on Escape key press (only when not uploading)
  useEscapeKey(onClose, isOpen && !uploading);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen || !documentType) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (isValidFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };

  const isValidFile = (f: File): boolean => {
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    return validTypes.includes(f.type) && f.size <= 10 * 1024 * 1024;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (isValidFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  const handleSubmit = async () => {
    if (file) {
      await onUpload(file, notes);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 transform transition-all">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Téléverser une nouvelle version
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Document:</span> {DOCUMENT_TYPE_LABELS[documentType]}
            </p>
          </div>

          {/* Zone de drop */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-10 h-10 text-primary-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="ml-4 text-gray-400 hover:text-red-500"
                >
                  <X size={20} />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  Glissez-déposez votre fichier ici ou{' '}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    parcourir
                  </button>
                </p>
                <p className="text-sm text-gray-500">PDF, DOC, DOCX (max 10 MB)</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Décrivez les modifications apportées dans cette version..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">{notes.length}/500 caractères</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!file || uploading}
              className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Téléversement...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Téléverser
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal de prévisualisation de document
interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: InstitutionalDocument | null;
  version: InstitutionalDocumentVersion | null;
  onDownload: (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  document,
  version,
  onDownload,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Track blob URL in ref for cleanup
  const blobUrlRef = useRef<string | null>(null);

  // Close modal on Escape key press
  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    // Cleanup previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (!isOpen || !document || !version) {
      setPreviewUrl(null);
      setError(null);
      setLoading(true);
      return;
    }

    // Create AbortController for cleanup
    const abortController = new AbortController();

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use secure fetch with Authorization header instead of URL token
        const blob = await fetchDocumentPreview(document.id, version.id);

        // Check if component was unmounted
        if (abortController.signal.aborted) return;

        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        if (abortController.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      abortController.abort();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, document, version]);

  if (!isOpen || !document || !version) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="flex h-full w-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {version.originalFileName}
                </h3>
                <p className="text-sm text-gray-500">
                  Version {version.versionNumber} • {formatFileSize(version.fileSize)} • {version.uploadedByName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden bg-gray-100 p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-3" />
                  <p className="text-gray-600">Chargement du document...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <p className="text-gray-900 font-medium mb-2">Erreur de chargement</p>
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-lg border border-gray-200 bg-white"
                title={version.originalFileName}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileWarning className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Format non supporté pour la prévisualisation</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={() => onDownload(document, version)}
              className="flex items-center gap-2 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Download size={16} />
              Télécharger
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant de carte de document
interface DocumentCardProps {
  document: InstitutionalDocument;
  canManage: boolean;
  onUpload: (type: InstitutionalDocumentType) => void;
  onDownload: (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => void;
  onPreview: (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => void;
  onSetCurrent: (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => void;
  onDelete: (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  canManage,
  onUpload,
  onDownload,
  onPreview,
  onSetCurrent,
  onDelete,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const hasDocument = document.currentVersion !== undefined;
  const description = DOCUMENT_TYPE_DESCRIPTIONS[document.documentType];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-lg ${hasDocument ? 'bg-green-100' : 'bg-gray-100'}`}>
              {hasDocument ? (
                <FileCheck className="w-5 h-5 text-green-600" />
              ) : (
                <FileText className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{document.label}</h3>
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasDocument && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                <CheckCircle2 size={12} />
                Version {document.currentVersion?.versionNumber}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Current Version Info */}
      {hasDocument && document.currentVersion && (
        <div className="p-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <FileText size={14} />
                {document.currentVersion.originalFileName}
              </span>
              <span className="text-gray-400">•</span>
              <span>{formatFileSize(document.currentVersion.fileSize)}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onPreview(document, document.currentVersion!)}
                className="flex items-center gap-1 text-gray-600 hover:text-primary-600 text-sm font-medium transition-colors"
                title="Visualiser le document"
              >
                <Eye size={14} />
                Visualiser
              </button>
              <button
                onClick={() => onDownload(document, document.currentVersion!)}
                className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                <Download size={14} />
                Télécharger
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User size={12} />
              {document.currentVersion.uploadedByName}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {formatDate(document.currentVersion.createdAt)}
            </span>
          </div>
          {document.currentVersion.notes && (
            <p className="mt-2 text-sm text-gray-600 italic">
              "{document.currentVersion.notes}"
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-4 flex items-center justify-between">
        {canManage ? (
          <button
            onClick={() => onUpload(document.documentType)}
            className="flex items-center gap-2 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Upload size={16} />
            {hasDocument ? 'Nouvelle version' : 'Téléverser'}
          </button>
        ) : (
          <div />
        )}

        {document.versions.length > 1 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
          >
            <History size={16} />
            Historique ({document.versions.length})
            {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {/* Version History */}
      {showHistory && document.versions.length > 1 && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Historique des versions</h4>
            <div className="space-y-2">
              {document.versions.map((version) => (
                <div
                  key={version.id}
                  className={`p-3 rounded-lg border ${
                    version.isCurrentVersion
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        version.isCurrentVersion
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        v{version.versionNumber}
                      </span>
                      <span className="text-sm text-gray-900">{version.originalFileName}</span>
                      {version.isCurrentVersion && (
                        <span className="text-xs text-primary-600 font-medium">(actuelle)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onPreview(document, version)}
                        className="p-1.5 text-gray-500 hover:text-primary-600 rounded"
                        title="Visualiser"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => onDownload(document, version)}
                        className="p-1.5 text-gray-500 hover:text-primary-600 rounded"
                        title="Télécharger"
                      >
                        <Download size={16} />
                      </button>
                      {!version.isCurrentVersion && canManage && (
                        <>
                          <button
                            onClick={() => onSetCurrent(document, version)}
                            className="p-1.5 text-gray-500 hover:text-green-600 rounded"
                            title="Définir comme version actuelle"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => onDelete(document, version)}
                            className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                            title="Supprimer cette version"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>{version.uploadedByName}</span>
                    <span>•</span>
                    <span>{formatDate(version.createdAt)}</span>
                    <span>•</span>
                    <span>{formatFileSize(version.fileSize)}</span>
                  </div>
                  {version.notes && (
                    <p className="mt-2 text-xs text-gray-600 italic">"{version.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Page principale
export const DocumentsInstitutionnelsPage: React.FC = () => {
  const { tokens } = useAuth();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.OFFICIAL_DOCUMENTS_MANAGE);
  const token = tokens?.accessToken;

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<InstitutionalDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [uploadModal, setUploadModal] = useState<{
    isOpen: boolean;
    documentType: InstitutionalDocumentType | null;
  }>({ isOpen: false, documentType: null });
  const [uploading, setUploading] = useState(false);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    document: InstitutionalDocument | null;
    version: InstitutionalDocumentVersion | null;
  }>({ isOpen: false, document: null, version: null });

  const [restoreModal, setRestoreModal] = useState<{
    isOpen: boolean;
    document: InstitutionalDocument | null;
    version: InstitutionalDocumentVersion | null;
  }>({ isOpen: false, document: null, version: null });

  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    document: InstitutionalDocument | null;
    version: InstitutionalDocumentVersion | null;
  }>({ isOpen: false, document: null, version: null });

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInstitutionalDocuments();
      setDocuments(data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des documents';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Clear messages after delay
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    // Error messages auto-hide after 10 seconds (longer than success messages)
    // to give users more time to read important error information
    const timer = setTimeout(() => setError(null), 10000);
    return () => clearTimeout(timer);
  }, [error]);

  // Handlers
  const handleUpload = async (file: File, notes: string) => {
    if (!token || !uploadModal.documentType) return;
    setUploading(true);
    try {
      const response = await uploadInstitutionalDocument(
        uploadModal.documentType,
        file,
        notes || undefined,
      );
      setSuccess(response.message);
      setUploadModal({ isOpen: false, documentType: null });
      await loadDocuments();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du téléversement';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: InstitutionalDocument, version: InstitutionalDocumentVersion) => {
    try {
      // Use secure download with Authorization header
      await downloadDocumentVersion(doc.id, version.id, version.originalFileName);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du téléchargement';
      setError(errorMessage);
    }
  };

  const handleSetCurrent = async () => {
    if (!token || !restoreModal.document || !restoreModal.version) return;
    try {
      const response = await setCurrentVersion(
        restoreModal.document.id,
        restoreModal.version.id,
      );
      setSuccess(response.message);
      setRestoreModal({ isOpen: false, document: null, version: null });
      await loadDocuments();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la restauration';
      setError(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!token || !deleteModal.document || !deleteModal.version) return;
    try {
      const response = await deleteVersion(
        deleteModal.document.id,
        deleteModal.version.id,
      );
      setSuccess(response.message);
      setDeleteModal({ isOpen: false, document: null, version: null });
      await loadDocuments();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Documents Officiels</h1>
        <p className="text-gray-500 mt-1">
          Gérez les documents officiels de votre établissement avec un historique complet des versions.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 transition-colors"
            aria-label="Fermer le message d'erreur"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-400 hover:text-green-600 transition-colors"
            aria-label="Fermer le message de succes"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Document Cards */}
      <div className="grid gap-6">
        {documents.map((doc) => (
          <DocumentCard
            key={doc.documentType}
            document={doc}
            canManage={canManage}
            onUpload={(type) => setUploadModal({ isOpen: true, documentType: type })}
            onDownload={handleDownload}
            onPreview={(d, v) => setPreviewModal({ isOpen: true, document: d, version: v })}
            onSetCurrent={(d, v) => setRestoreModal({ isOpen: true, document: d, version: v })}
            onDelete={(d, v) => setDeleteModal({ isOpen: true, document: d, version: v })}
          />
        ))}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModal.isOpen}
        documentType={uploadModal.documentType}
        onClose={() => setUploadModal({ isOpen: false, documentType: null })}
        onUpload={handleUpload}
        uploading={uploading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Supprimer cette version"
        message={
          <>
            Êtes-vous sûr de vouloir supprimer la version {deleteModal.version?.versionNumber} du document{' '}
            <strong>{deleteModal.document?.label}</strong> ?
            <br />
            <span className="text-red-600 font-medium">Cette action est irréversible.</span>
          </>
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ isOpen: false, document: null, version: null })}
      />

      {/* Restore Confirmation Modal */}
      <ConfirmModal
        isOpen={restoreModal.isOpen}
        title="Restaurer cette version"
        message={
          <>
            Êtes-vous sûr de vouloir définir la version {restoreModal.version?.versionNumber} comme version actuelle du document{' '}
            <strong>{restoreModal.document?.label}</strong> ?
          </>
        }
        confirmText="Restaurer"
        cancelText="Annuler"
        variant="info"
        onConfirm={handleSetCurrent}
        onCancel={() => setRestoreModal({ isOpen: false, document: null, version: null })}
      />

      {/* Preview Modal */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, document: null, version: null })}
        document={previewModal.document}
        version={previewModal.version}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default DocumentsInstitutionnelsPage;
